import PropTypes from 'prop-types';
import find from 'lodash/find';
import React, {
  useMemo,
} from 'react';
import {
  useTranslation,
} from 'react-i18next';
import Stack from '../../common/components/primitives/Stack';
import Sidebar from '../../common/components/primitives/Sidebar';
import Spacer from '../../common/components/primitives/Spacer';
import Text from '../../common/components/base/Text';
import Skeleton from '../../common/components/Skeleton';
import Card from '../../common/components/Card';
import Image from '../../common/components/base/Image';
import AnswersSheetsList from './AnswersSheetsList';
import AnswersSheetButtons from './AnswersSheetButtons';

// TODO: Maybe we could put it to "common/constans" in the future. I'm just not sure where for now
export const TOKEN_STATE__MISSING = 'MISSING';
export const TOKEN_STATE__EXPIRED = 'EXPIRED';
export const TOKEN_STATE__COMPLETED = 'COMPLETED';
export const TOKEN_STATE__ACTIVE = 'ACTIVE';

const getTitle = ({
  t,
  welcomeTitle,
  recipientName,
}) => {
  let title = welcomeTitle || t('welcome');

  if (recipientName) {
    title = `${title} ${recipientName}`;
  }

  return title;
};

const getDescription = ({
  t,
  welcomeMsg,
  currentAnswersSheetState,
  tokenState,
}) => {
  if (tokenState === TOKEN_STATE__MISSING) {
    return t('tokenMissing');
  }

  if (tokenState === TOKEN_STATE__EXPIRED) {
    return t('tokenExpired');
  }

  if (tokenState === TOKEN_STATE__COMPLETED) {
    return t('completed');
  }

  if (
    currentAnswersSheetState === 'INITIAL' ||
    currentAnswersSheetState === 'SCHEDULED'
  ) {
    return welcomeMsg;
  }

  return t('continued');
};

const Activity = ({
  recipientName,
  milestoneName,
  // clinicianName,
  // clinicianPhotoUrl,
  projectName,
  projectLogoUrl,
  welcomeTitle,
  welcomeMsg,
  answersSheets,
  currentProjectLanguage,
  currentAnswersSheetId,
  loading,
  tokenState,
}) => {
  const {
    t,
  } = useTranslation();
  const currentAnswersSheet = useMemo(() => {
    return find(answersSheets, {
      id: currentAnswersSheetId,
    });
  }, [
    answersSheets,
    currentAnswersSheetId,
  ]);
  const currentAnswersSheetState =
    currentAnswersSheet && currentAnswersSheet.state;
  return (
    <Stack>
      <Spacer>
        <Sidebar
          sidebar={
            projectLogoUrl && (
              <Image
                src={projectLogoUrl}
                alt={projectName || 'Project logo'}
                height="100px"
              />
            )
          }
        >
          <Stack space={0}>
            {/* {clinicianPhotoUrl && (
              <Image
                src={clinicianPhotoUrl}
                alt={clinicianName || 'Clinician photo'}
              />
            )} */}
            <Text.Paragraph importance="high">
              {loading ? (
                <Skeleton width={200} />
              ) : (
                getTitle({
                  t,
                  welcomeTitle,
                  recipientName,
                  currentAnswersSheetState,
                })
              )}
            </Text.Paragraph>
            <Text.Paragraph
              size="small"
              importance="low"
            >
              {loading ? (
                <Skeleton />
              ) : (
                getDescription({
                  t,
                  welcomeMsg,
                  currentAnswersSheetState,
                  tokenState,
                })
              )}
            </Text.Paragraph>
          </Stack>
        </Sidebar>
      </Spacer>
      <Card loading={loading}>
        {currentAnswersSheetId && (
          <Stack>
            {milestoneName && (
              <Stack>
                <Text.Paragraph
                  size="small"
                  importance="low"
                >
                  Milestone
                </Text.Paragraph>
                <Text.Span size="large">{milestoneName}</Text.Span>
              </Stack>
            )}
            <Stack>
              <AnswersSheetButtons
                currentProjectLanguage={currentProjectLanguage}
                answersSheetId={currentAnswersSheetId}
              />
              <AnswersSheetsList
                answersSheets={answersSheets}
                currentAnswersSheetId={currentAnswersSheetId}
              />
            </Stack>
          </Stack>
        )}
      </Card>
    </Stack>
  );
};

Activity.propTypes = {
  welcomeTitle: PropTypes.string,
  welcomeMsg: PropTypes.string,
  currentProjectLanguage: PropTypes.string,
  recipientName: PropTypes.string,
  milestoneName: PropTypes.string,
  projectName: PropTypes.string,
  projectLogoUrl: PropTypes.string,
  // clinicianName: PropTypes.string,
  // clinicianPhotoUrl: PropTypes.string,
  answersSheets: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      state: PropTypes.string,
      questionnaire: PropTypes.shape({
        name: PropTypes.string,
      }),
    }),
  ),
  currentAnswersSheetId: PropTypes.string,
  loading: PropTypes.bool,
  tokenState: PropTypes.oneOf([
    TOKEN_STATE__MISSING,
    TOKEN_STATE__EXPIRED,
    TOKEN_STATE__COMPLETED,
    TOKEN_STATE__ACTIVE,
  ]),
};

Activity.defaultProps = {
  welcomeTitle: null,
  welcomeMsg: null,
  currentProjectLanguage: null,
  recipientName: null,
  milestoneName: null,
  projectName: null,
  projectLogoUrl: null,
  // clinicianName: null,
  // clinicianPhotoUrl: null,
  answersSheets: [],
  currentAnswersSheetId: null,
  loading: false,
  tokenState: null,
};

export default Activity;
